import clsx from 'clsx';
import { useState, Activity, useEffect } from 'react';

import {
  IconEye,
  IconXboxX,
  IconUsers,
  IconSearch,
  IconLockOpen,
  IconCheckbox,
  IconUserCheck,
  IconUsersGroup,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import { CommonStatusConfig } from '@/constants/constant';
import { ECommonStatus, ERestoreEntityType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { blockUnblockRecordAPI } from '@/service/commonService';
import { getparentList, getParentSummary } from '@/service/schoolService';
import useCommonStore from '@/store/useCommonStore';
import {
  IParentData,
  ISelectedRowValue,
  IParentSummaryData,
  TParentSummaryItem,
  IParentFilterValues,
} from '@/types';
import { getAssetUrl, formatLastActive, getStartOfMonthUnix } from '@/utils';

import ParentDetailsModal from './modals/ParentDetailsModal';
import ParentFilterModal from './modals/ParentFilterModal';

const PARENT_SUMMARY_CONFIG = [
  {
    key: 'totalParents',
    icon: <IconUsers color="#717784" />,
    label: 'parent.summary-cards.total-parents',
  },
  {
    key: 'activeParents',
    icon: <IconUserCheck color="#717784" />,
    label: 'parent.summary-cards.active-parents',
  },
  {
    key: 'activeFreeUsers',
    icon: <IconUsersGroup color="#717784" />,
    label: 'parent.summary-cards.active-free-users',
  },
  {
    key: 'activeSubscribedUsers',
    icon: <IconCheckbox color="#717784" />,
    label: 'parent.summary-cards.active-subscribed-users',
  },
];

const Parent = () => {
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { formatText } = useFormatText();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);

  const [parents, setParents] = useState<IParentData[]>([]);
  const {
    loadingTable,
    setLoadingTable,
    loadingSummary,
    setLoadingSummary,
    loadingModal,
    setLoadingModal,
  } = useCommonStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [selectedParent, setSelectedParent] =
    useState<ISelectedRowValue | null>(null);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);
  const [isParentDetailsOpen, setIsParentDetailsOpen] = useState(false);

  const [statsData, setStatsData] = useState<TParentSummaryItem[]>(
    PARENT_SUMMARY_CONFIG.map((item) => ({
      ...item,
      value: '0',
      trend: '0%',
      trendLabel: 'common.this-month',
      trendDirection: 'STABLE',
      isPositive: true,
    }))
  );
  const [filterValues, setFilterValues] = useState<IParentFilterValues>({
    status: '',
    linkedChildren: '',
  });

  const pageTitle = formatText('parent.title');
  const userManagement = formatText('user-management.title');

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: userManagement }, { label: pageTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, userManagement]);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    const response = await getParentSummary({
      month: getStartOfMonthUnix(),
    });
    if (response?.status) {
      const apiData = response?.data as IParentSummaryData;
      const mapped = PARENT_SUMMARY_CONFIG.map((item) => {
        const data = apiData[item.key as keyof IParentSummaryData];
        return {
          ...item,
          value: (data?.total || 0).toLocaleString(),
          trend: `${data?.percentage || 0}%`,
          trendLabel: 'common.this-month',
          trendDirection: data?.trend?.toUpperCase() || 'STABLE',
          isPositive: data?.isPositive || false,
        };
      });
      setStatsData(mapped);
    }
    setLoadingSummary(false);
  };

  const fetchParents = async () => {
    setLoadingTable(true);

    const response = await getparentList({
      search: debouncedSearch || undefined,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      order: sortColumn ? { [sortColumn]: sortDirection } : undefined,
      status: filterValues?.status || undefined,
      linked_children: filterValues?.linkedChildren || undefined,
    });
    if (response?.status) {
      setParents(response?.data || []);
      setTotal(response?.total || 0);
    }
    setLoadingTable(false);
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterValues]);

  useEffect(() => {
    fetchParents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    sortColumn,
    sortDirection,
    filterValues,
  ]);

  const handlefetchdata = () => {
    fetchParents();
    fetchSummary();
  };

  const handleBlockParent = async (parent = selectedParent) => {
    if (!parent) return;
    setLoadingModal(true);
    try {
      const params = {
        id: parent.id,
        record_type: ERestoreEntityType.PARENT,
        is_block: parent.status === ECommonStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);
      if (response?.status) {
        handlefetchdata();
      } else {
        console.error(response?.message || 'Failed to update parent status');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingModal(false);
      setSelectedParent(null);
      setIsOpenBlockModal(false);
    }
  };

  const handleFilterApply = (filters: IParentFilterValues) => {
    setFilterValues(filters);
  };

  //sorting handler
  const handleSort = (key: string) => {
    if (sortColumn === key) {
      setSortDirection(
        sortDirection === 'ASC' ? 'DESC' : sortDirection === 'DESC' ? '' : 'ASC'
      );
      if (sortDirection === 'DESC') setSortColumn('');
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'parent.columns.name',
      key: 'name',
      sorter: true,
      handleSort: () => handleSort('name'),
      align: 'left',
      render: (row: IParentData) =>
        row.name ? (
          <div className="flex items-center gap-2">
            <AvatarGroupList
              list={[
                {
                  id: row?.id,
                  name: row?.name,
                  image: getAssetUrl(row?.asset) || row?.user_img,
                },
              ]}
              size={40}
              avatarClassName="w-10 h-10 text-xs"
            />
            <span className="font-medium capitalize">{row?.name}</span>
          </div>
        ) : (
          <span
            className="flex h-10 w-10 items-center justify-center"
            aria-label="No data"
          >
            -
          </span>
        ),
    },
    {
      title: 'common.field.email',
      key: 'email',
      sorter: true,
      handleSort: () => handleSort('email'),
    },
    {
      title: 'parent.columns.linked-children',
      key: 'linked_children',
      sorter: true,
      handleSort: () => handleSort('linked_children'),
      render: (row: IParentData) => row?.linked_children ?? 0,
    },
    {
      title: 'parent.columns.status',
      key: 'status',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: IParentData) => {
        const config =
          CommonStatusConfig[row?.status as keyof typeof CommonStatusConfig];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'parent.columns.last-active',
      key: 'last_active',
      sorter: true,
      handleSort: () => handleSort('last_active'),
      render: (row: IParentData) =>
        formatLastActive(row?.last_active ?? undefined, formatText),
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '5%',
      align: 'center',
      render: (row: IParentData) => {
        const isBlocked = row?.status === ECommonStatus.IN_ACTIVE;
        const parent = {
          name: row?.name,
          id: row?.id,
          status: row?.status,
        };

        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedParent({ name: parent?.name, id: parent?.id });
              setIsParentDetailsOpen(true);
            },
          },
          {
            key: isBlocked ? 'unblock' : 'block',
            label: isBlocked
              ? 'common.actions.unblock'
              : 'common.actions.block',
            // isDanger: !isBlocked && true,
            icon: isBlocked ? (
              <IconLockOpen size={18} />
            ) : (
              <IconXboxX size={18} />
            ),
            onClick: () => {
              if (isBlocked) {
                handleBlockParent(parent);
              } else {
                setSelectedParent(parent);
                setIsOpenBlockModal(true);
              }
            },
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const isFilterApplied =
    !!filterValues?.linkedChildren || !!filterValues?.status;

  const isEmptyList =
    !loadingTable &&
    parents?.length === 0 &&
    search?.length === 0 &&
    debouncedSearch?.length === 0 &&
    !isFilterApplied;

  return (
    <>
      {!isEmptyList && (
        <>
          <div className="flex flex-wrap justify-start items-center gap-2.5 mb-6 ps-1">
            <Input
              name="search"
              placeholder={formatText('parent.search-placeholder')}
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              leftIcon={{
                icon: <IconSearch size={20} className="text-neutral-500" />,
              }}
              className="max-w-77 md:min-w-77 w-full"
              inputClassName="py-[13px]"
            />
            <div className="relative">
              <Button
                icon={<IconAdjustments size={20} />}
                label={formatText('common.actions.filter')}
                variant="outline"
                className={clsx(
                  'px-4 h-12 cursor-pointer',
                  isFilterApplied && 'border-brand-default! text-brand-default!'
                )}
                onClick={() => setIsFilterModalOpen(true)}
              />
              {isFilterApplied && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            {statsData?.map((stat, index) => (
              <SummaryCard
                key={index}
                variant="dashboard"
                label={formatText(stat.label)}
                value={stat?.value}
                icon={stat?.icon}
                trendDirection={stat?.trendDirection}
                isPositive={stat?.isPositive}
                trendLabel={
                  stat?.trendLabel ? formatText(stat.trendLabel) : undefined
                }
                trend={stat?.trend}
                isLoading={loadingSummary}
              />
            ))}
          </div>
        </>
      )}
      <div className="mt-6">
        <CustomTable
          isLoading={loadingTable}
          dataSource={parents}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          fieldName={sortColumn}
          sortDirection={sortDirection}
          isEmpty={isEmptyList}
          emptyTitle={formatText('parent.not-found-title')}
          emptyDescription={formatText('parent.not-found-desc')}
          isEmptySearch={
            parents?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          onRowClick={(id) => {
            const parent = parents.find((item) => item.id === id);
            if (parent) {
              setSelectedParent({ name: parent?.name, id: parent?.id });
              setIsParentDetailsOpen(true);
            }
          }}
        />
      </div>

      <Activity mode={isOpenBlockModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isOpenBlockModal}
          setIsOpen={setIsOpenBlockModal}
          title="parent.actions.block-parent-title"
          descriptionPrefix="parent.actions.block-parent-prefix"
          descriptionSuffix="parent.actions.block-parent-suffix"
          entityName={selectedParent?.name}
          entityFallback="parent.actions.this-parent-account"
          confirmLabel={formatText('parent.actions.block-parent')}
          onConfirm={handleBlockParent}
          isLoading={loadingModal}
          isDestructive
        />
      </Activity>
      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <ParentFilterModal
          setIsOpen={setIsFilterModalOpen}
          onApply={handleFilterApply}
          initialValues={filterValues}
        />
      </Activity>
      <Activity mode={isParentDetailsOpen ? 'visible' : 'hidden'}>
        <ParentDetailsModal
          setIsOpen={setIsParentDetailsOpen}
          selectedParentId={selectedParent?.id}
          onStatusUpdate={handlefetchdata}
        />
      </Activity>
    </>
  );
};

export default Parent;
